// ScopeChainsSection — the READ list of the accountability chains at one scope
// (the shipped ResponsibilityAssignmentList over the live chains), every
// level's page section and peek body. The wire carries ordered ids only
// (ADR-026 §C); the FEW people named are the person lookup's rows of given ids
// on the `members` rule — membership-gated, so the place's every reader resolves
// them (ADR-078 §6) — never a member directory downloaded whole. The rows are
// CONTENT: each person on a chain is a quiet `PersonRef` (a press peeks them —
// ADR-036 §6 — when the lookup's row says this viewer may open them, `can_open`,
// through the HOST's `peekPerson`: a page opens, a sheet follows, a focused flow
// passes none and the names stay plain); the card's own act (Manage responsibilities — the entity's registry) is
// the one door to the editor, never a row (architect-ruled at the team's live
// look). Mount only while the field-force module is on.

import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useScopeChains, type ChainLevel, type ChainScopeId } from '../../hooks/useChains.js';
import { useNamedPeople } from '../../hooks/useUsers.js';
import { Alert } from '../ui/Alert.js';
import { Spinner } from '../ui/Spinner.js';
import { assignmentRowLabels, toAssignmentItems } from './accountabilityDisplay.js';
import { PersonRef } from './PersonRef.js';
import { ResponsibilityAssignmentList } from './ResponsibilityAssignmentList.js';
import type { ResponsibilityAssignmentMember } from './ResponsibilityAssignmentRow.js';

export interface ScopeChainsSectionProps {
  projectId: string;
  scope: { level: ChainLevel; id: ChainScopeId };
  /** The host's peek for a person named here (a page opens it, a sheet follows to it); absent — no peek hosted. */
  peekPerson?: (userId: string) => void;
}

export function ScopeChainsSection({ projectId, scope, peekPerson }: ScopeChainsSectionProps) {
  const { t } = useTranslation('common');
  const chainsQ = useScopeChains(projectId, scope.level, scope.id);
  const ids = useMemo(() => [...new Set((chainsQ.data?.items ?? []).flatMap((c) => c.member_ids))], [chainsQ.data]);
  const { byId } = useNamedPeople(projectId, ids);
  const nameOf = useCallback(
    (id: string): string => {
      const u = byId.get(id);
      return u ? `${u.first_name} ${u.last_name}`.trim() : '…';
    },
    [byId],
  );
  // Where the names may be doors (a peek host), each line keeps the touch target's floor — 24 px, the accessibility
  // minimum: the names stay one group beside the lead's avatar (a fingertip line each scattered them — architect's
  // eye; a team's member ROWS keep the fingertip, FRONTEND-PATTERNS §Row interaction); a focused flow's plain names
  // keep the card's own rhythm.
  const renderMember = useCallback(
    (m: ResponsibilityAssignmentMember) => {
      const ref = <PersonRef projectId={projectId} id={m.id} name={m.name} canOpen={byId.get(m.id)?.can_open ?? false} onPeek={peekPerson && (() => peekPerson(m.id))} quiet />;
      return peekPerson ? <span className="flex min-w-0 flex-1 items-center pointer-coarse:min-h-6">{ref}</span> : ref;
    },
    [projectId, byId, peekPerson],
  );
  if (chainsQ.isPending) {
    return (
      <div className="flex min-h-[6rem] items-center justify-center" role="status" aria-live="polite">
        <Spinner />
      </div>
    );
  }
  // A failed FIRST read; a failed refresh keeps the rows it had (COMPONENTS §States).
  if (chainsQ.isError && chainsQ.data === undefined) return <Alert variant="danger">{t('chains.error')}</Alert>;
  return <ResponsibilityAssignmentList items={toAssignmentItems(chainsQ.data?.items ?? [], nameOf)} renderMember={renderMember} empty={t('chains.empty')} rowLabels={assignmentRowLabels(t)} />;
}
